import {
  AutoCompleteUncontrolled,
  ISelectType,
  OverflowTooltip,
  Table,
  useFetch,
} from "@4uhub/lib4uhub";
import { Box } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import MainApi from "../../../../services/mainApi.service";
import { IBanLists } from "../../../../models/ban-list";
import { listMainGenerics } from "../../../../services/generic.service";

const TICKET_SERVICE_BAN_LIST_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketServiceBanList/";
const ticketServiceBanListService = new MainApi<IBanLists>(
  TICKET_SERVICE_BAN_LIST_ROUTE,
);

const translationPath = "chatBot.page.service.banList.";

const BanListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();

  const [types, setTypes] = useState<ISelectType[]>([]);
  const [selectedType, setSelectedType] = useState<ISelectType | null>(null);
  const { sendRequest: fetchTypes } = useFetch<
    { Identifier: string },
    ISelectType[]
  >(listMainGenerics);

  const fetchBanListTypes = useCallback(async () => {
    const { data, success } = await fetchTypes({
      Identifier: "TicketServiceBanListType",
    });
    if (data && success) {
      setTypes(data);
    }
  }, [fetchTypes]);

  useEffect(() => {
    fetchBanListTypes();
  }, [fetchBanListTypes]);

  const selectedTypeId = useMemo(
    () => searchParams.get("TicketServiceBanListTypeId") ?? "",
    [searchParams],
  );

  useEffect(() => {
    setSelectedType(types.find((type) => type.id === selectedTypeId) ?? null);
  }, [selectedTypeId, types]);

  const changeTypeHandler = useCallback(
    (value: ISelectType | null) => {
      setSelectedType(value);

      const newParams = Object.fromEntries(searchParams.entries());
      if (value?.id) {
        newParams.TicketServiceBanListTypeId = value.id;
      } else {
        delete newParams.TicketServiceBanListTypeId;
      }

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const serviceProps = useMemo(
    () => ({
      TicketServiceBanListTypeId: selectedTypeId || undefined,
    }),
    [selectedTypeId],
  );

  const columns: GridColDef<IBanLists>[] = [
    {
      field: "phoneNumber",
      headerName: t(translationPath + "phone_number") || "Phone Number",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Date and time",
      renderCell: ({ row: { created } }) => {
        if (!created) return "-";
        const date = new Date(created).toLocaleString();
        return (
          <OverflowTooltip
            text={date}
            tooltip={date}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "ticketService",
      headerName: t(translationPath + "ticket_service") || "Ticket Service",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "ticketServiceBanListType",
      headerName: t(translationPath + "type") || "Type",
      renderCell: ({ row: { ticketServiceBanListType } }) => {
        if (!ticketServiceBanListType) return "-";
        return (
          <OverflowTooltip
            text={ticketServiceBanListType.name}
            tooltip={ticketServiceBanListType.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<IBanLists>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  return (
    <Table<IBanLists>
      showDefaultMacroFilters={false}
      service={ticketServiceBanListService}
      columns={columns}
      serviceProps={serviceProps}
      title={t(translationPath + "ban_lists")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      searchInputPlaceHolder={t(translationPath + "search")}
      macroFilters={() => (
        <Box sx={{ width: 300 }}>
          <AutoCompleteUncontrolled
            label={t(translationPath + "type") || "Type"}
            onValueSelected={changeTypeHandler}
            value={selectedType}
            options={types}
            clearable
            size="small"
            fullWidth
          />
        </Box>
      )}
      addButtonLabel={t(translationPath + "add_ban_list") || "Ban List"}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default BanListPage;
